import { createCollector, type Collector } from './collector';
import { createConsentBadge } from './badge';
import { CONSENT_KEY, readConsent, writeConsent, randomId, storedVisitId, visitIdentity, type ConsentChoice, type VisitIdentity } from './consent';
import { createTransport, endpointFor, type Transport } from './transport';
import { clearVendorCookies, startVendors, vendorConfig } from './vendors';
import {createVendorGoals} from './vendor-goals';
import { internalBrowser } from './internal';

type View = Window & typeof globalThis;
const WITHDRAWAL_KEY = 'bb.analytics.pending-withdrawal';
declare global {
  interface Window {
    __BB_ANALYTICS__?: { readonly consent: ConsentChoice | null; ask(): void; failure: Collector['failure']; bridgeExpected(visible:boolean):void; readonly deliveryError: string | null };
  }
}

export function installAnalytics(view: View): void {
  const endpoint = developmentEndpoint() ?? endpointFor(view.location.hostname);
  if (!endpoint) return;
  const store = safeStorage(view);
  const vendors = vendorConfig(view.location.hostname);
  for(const node of view.document.querySelectorAll<HTMLElement>('[data-bb-consent-default]'))node.hidden=Boolean(vendors.replay);
  for(const node of view.document.querySelectorAll<HTMLElement>('[data-bb-consent-replay]'))node.hidden=!vendors.replay;
  const enabled = import.meta.env.VITE_BB_ANALYTICS_ENABLED === 'true';
  const settings = view.document.querySelector<HTMLElement>('[data-bb-privacy-settings]');
  let choice = readConsent(store, vendors.revision);
  // Служебный браузер Руководителя не учитывается и не спрашивается (internal.ts).
  const internal = internalBrowser(view.location, store, view.history ?? null);
  let visit: VisitIdentity | null = null;
  let sessionId: string | null = null;
  let collector: Collector | null = null;
  let stopVendors: (() => void) | null = null;
  let vendorGoals: ReturnType<typeof createVendorGoals> | null = null;
  let deliveryError: string | null = null;
  const rawTransport = createTransport(view, endpoint.url, reason => { deliveryError = reason; });
  const transport: Transport = {
    push(event) { if (choice === 'granted') {rawTransport.push({...event, consent_revision:vendors.revision});vendorGoals?.push(event);} },
    flush: options => rawTransport.flush(options),
    get pending() { return rawTransport.pending; },
  };
  const identity = () => {
    if (choice !== 'granted') return null;
    try {
      visit ??= visitIdentity(store, view);
      sessionId ??= randomId(view);
      return {visitId:visit.id, sessionId, returning:visit.returning};
    } catch { return null; }
  };
  const retryWithdrawal = async (explicitId?:string): Promise<void> => {
    let id: string | null = explicitId ?? null;
    if(!id)try { id = store?.getItem(WITHDRAWAL_KEY) ?? null; } catch { return; }
    if (!id || !/^[0-9a-f]{32}$/.test(id)) return;
    try {
      const response = await view.fetch(endpoint.url + '/withdraw', {method:'POST',
        headers:{'Content-Type':'text/plain'}, body:JSON.stringify({visit_id:id}),
        credentials:'omit', referrerPolicy:'no-referrer', keepalive:true});
      if (response.ok && store?.getItem(WITHDRAWAL_KEY) === id) store.removeItem(WITHDRAWAL_KEY);
    } catch { /* Retain the essential withdrawal request for the next visit. */ }
  };
  void retryWithdrawal();
  const start = (): void => {
    if (!enabled || settings || collector || !identity()) return;
    stopVendors = startVendors(view, vendors);
    vendorGoals = createVendorGoals(view,vendors);
    collector = createCollector({view, transport, contour:endpoint.contour, identity});
    collector.start();
  };
  const updateSettings = (): void => {
    if (!settings) return;
    settings.hidden = !enabled && choice === null && !storedVisitId(store);
    const accept=settings.querySelector<HTMLButtonElement>('[data-privacy-accept]');
    if(accept) accept.disabled=!enabled;
    for (const node of settings.querySelectorAll<HTMLElement>('[data-consent-status]')) node.hidden = node.dataset.consentStatus !== (choice ?? 'unset');
    for (const node of settings.querySelectorAll<HTMLElement>('[data-consent-provider]')) {
      node.hidden = node.dataset.consentProvider === 'ga4' ? !vendors.ga4 : node.dataset.consentProvider === 'webvisor' ? !vendors.replay : !vendors.metrica;
    }
  };
  const choose = (next: ConsentChoice, persist = true): void => {
    if (internal) next = 'denied';
    const oldId = visit?.id ?? storedVisitId(store);
    const hadVendors = Boolean(stopVendors && (vendors.ga4 || vendors.metrica));
    if (next === 'denied') {
      choice = next;
      collector?.stop(); collector = null;
      rawTransport.discard?.();
      stopVendors?.(); stopVendors = null;
      vendorGoals?.stop();vendorGoals=null;
      clearVendorCookies(view);
      if (oldId) {
        try { store?.setItem(WITHDRAWAL_KEY, oldId); } catch { /* The immediate request still runs below. */ }
      }
      visit = null; sessionId = null;
    } else choice = next;
    if (persist) writeConsent(store, next, Date.now(), vendors.revision);
    updateSettings();
    if (next === 'granted') start();
    else { void retryWithdrawal(oldId ?? undefined); if (hadVendors) view.location.reload(); }
  };
  const badge = createConsentBadge({view, onChoice:next=>choose(next)});
  if (settings) {
    settings.querySelector('[data-privacy-accept]')?.addEventListener('click',()=>choose('granted'));
    settings.querySelector('[data-privacy-decline]')?.addEventListener('click',()=>choose('denied'));
    updateSettings();
  }
  view.addEventListener('storage', event => {
    if (internal) return;
    if (event.key !== CONSENT_KEY && event.key !== null) return;
    const next = readConsent(store, vendors.revision);
    if (next !== choice) choose(next ?? 'denied', false);
  });
  // Отзыв той же дорогой, что отказ: прошлая история браузера снимается с учёта.
  if (internal) choose('denied');
  if (enabled && choice === 'granted') start();
  if (enabled && choice === null && badge) whenIntroComplete(view, () => {
    if (choice === null) badge.open();
  });
  view.__BB_ANALYTICS__ = {
    get consent() { return choice; },
    get deliveryError() { return deliveryError; },
    ask() { if(enabled) badge?.open(); },
    failure(name, props) { collector?.failure(name, props); },
    bridgeExpected(visible) {
      if(choice!=='granted')return;
      const flags=view.document.documentElement.dataset,value=String(visible);
      if(flags.bbBridgeExpected!==value)flags.bbBridgeExpected=value;
    },
  };
}

function developmentEndpoint(): {url:string;contour:'ru'|'com'} | null {
  if (!import.meta.env.DEV) return null;
  const raw = import.meta.env.VITE_BB_ANALYTICS_ENDPOINT;
  if (!raw) return null;
  try {
    const url = new URL(raw);
    if (!['127.0.0.1','localhost','[::1]'].includes(url.hostname)) return null;
    return {url:url.href,contour:'ru'};
  } catch { return null; }
}
function safeStorage(view: View): Storage | null {
  try { return view.localStorage; } catch { return null; }
}
export function whenIntroComplete(view: View, run: () => void): () => void {
  let done = false;
  let timer = 0;
  let delay = 0;
  const finish = (): void => {
    if (done) return;
    done = true; observer.disconnect(); view.clearTimeout(timer);
    delay = view.setTimeout(run, 1200);
  };
  const observer = new view.MutationObserver(() => {
    if (view.document.body.classList.contains('intro-complete')) finish();
  });
  if (view.document.body.classList.contains('intro-complete')) finish();
  else { observer.observe(view.document.body, {attributes:true,attributeFilter:['class']}); timer = view.setTimeout(finish,20000); }
  return () => { done = true; observer.disconnect(); view.clearTimeout(timer); view.clearTimeout(delay); };
}
